import { useEventListener } from '@vueuse/core';
import debounce from 'lodash/debounce';

export default function usePointerEvents({
  svg,
  zoomInstructions,
  selectedLocationEl,
  selectedPathEl,
  topHandle,
  rightHandle,
  bottomHandle,
  leftHandle,
  pathHandle1,
  pathHandle2,
  isPanning,
  isDragging,
  isPolyPointMoving,
  svgX,
  svgY,
  props,
  emit,
  svgViewBox,
  isPinching,
  isResizing,
  isPathNodeMoving,
}) {
  // Utility functions
  const { pan, screenToSvgCoordinates, zoomToPoint } = svgViewBox;

  const resize = (movementX, movementY, emitEvent) => {
    const ctm = svg.value.getScreenCTM();
    const movement = new DOMMatrix([0, 0, movementX, movementY, 0, 0]).multiply(ctm.inverse());
    const newDimensions = { ...props.selectedLocation };

    if (isResizing.value === 'up') {
      const depth = props.selectedLocation.depth + (movement.b - movement.d);
      if (depth > 1) {
        newDimensions.y -= (movement.b - movement.d);
        newDimensions.depth = depth;
      }
    } else if (isResizing.value === 'right') {
      const width = props.selectedLocation.width + movement.c - movement.a;
      if (width > 1) newDimensions.width = width;
    } else if (isResizing.value === 'down') {
      const depth = props.selectedLocation.depth + movement.d - movement.b;
      if (depth > 1) newDimensions.depth = depth;
    } else if (isResizing.value === 'left') {
      const width = props.selectedLocation.width + (movement.a - movement.c);
      if (width > 1) {
        newDimensions.x -= (movement.a - movement.c);
        newDimensions.width = width;
      }
    }
    emit(emitEvent, newDimensions);
  };

  const moveSelected = (movementX, movementY) => {
    if (!isDragging.value) return;

    const ctm = svg.value.getScreenCTM();
    const movement = new DOMMatrix([0, 0, movementX, movementY, 0, 0]).multiply(ctm.inverse());
    const { x, y } = props.selectedLocation;
    const newX = x + movement.c - movement.a;
    const newY = y + movement.d - movement.b;

    emit('locationMove', { x: newX, y: newY });
  };

  const moveSelectedPathNode = (movementX, movementY) => {
    if (!isPathNodeMoving.value) return;

    const ctm = svg.value.getScreenCTM();
    const movement = new DOMMatrix([0, 0, movementX, movementY, 0, 0]).multiply(ctm.inverse());
    const nodeKey = isPathNodeMoving.value === 'first' ? 'x1' : 'x2';
    const newX = props.selectedPath[nodeKey] + movement.c - movement.a;
    const newY = props.selectedPath[nodeKey.replace('x', 'y')] + movement.d - movement.b;

    emit('pathNodeMove', { node: isPathNodeMoving.value, x: newX, y: newY });
  };

  const movePolyPoint = (movementX, movementY) => {
    if (isPolyPointMoving.value === false) return;

    const ctm = svg.value.getScreenCTM();
    const movement = new DOMMatrix([0, 0, movementX, movementY, 0, 0]).multiply(ctm.inverse());
    const [x, y] = props.selectedLocation.points[isPolyPointMoving.value];
    const newX = x + movement.c - movement.a;
    const newY = y + movement.d - movement.b;

    emit('polyPointMove', { point: isPolyPointMoving.value, x: newX, y: newY });
  };

  const handleMove = (deltaX, deltaY) => {
    if (isPinching.value) return;
    if (isResizing.value) {
      resize(deltaX, deltaY, 'locationResize');
    } else if (isPolyPointMoving.value !== false) {
      movePolyPoint(deltaX, deltaY);
    } else if (!isDragging.value && !isPathNodeMoving.value) {
      isPanning.value = true;
      pan(-deltaX, -deltaY);
    } else if (isDragging.value && props.lockSelected && props.selectedLocation) {
      moveSelected(deltaX, deltaY);
    } else if (isPathNodeMoving.value && props.lockSelected && props.selectedPath) {
      moveSelectedPathNode(deltaX, deltaY);
    }
  };

  const handleEnd = (event, deltaX = 0, deltaY = 0) => {
    if (isResizing.value) {
      event.preventDefault();
      resize(deltaX, deltaY, 'locationResized');
      isResizing.value = false;
    } else if (isDragging.value) {
      emit('locationMoved', props.selectedLocation);
      isDragging.value = false;
    } else if (isPolyPointMoving.value !== false) {
      emit('polyPointMoved', isPolyPointMoving.value);
      isPolyPointMoving.value = false;
    } else if (isPathNodeMoving.value) {
      emit('pathNodeMoved');
      isPathNodeMoving.value = false;
    }
    isPanning.value = false;
  };

  const handlePointer = (event, eventType) => {
    if (eventType === 'down') {
      emit('svgClicked'); // Emit an event when the SVG is clicked
    } else if (eventType === 'move' && event.buttons !== 0) {
      handleMove(event.movementX, event.movementY); // Handle pointer movement
    } else if (eventType === 'up') {
      handleEnd(event, event.movementX, event.movementY); // Handle pointer release
    }
  };

  /**
   * Debounced function to hide zoom instructions after a delay.
   */
  const hideZoomInstructions = debounce(() => {
    if (zoomInstructions.value) {
      zoomInstructions.value.style.display = 'none';
    }
  }, 1000);

  /**
   * Initializes all pointer event listeners for the SVG and related elements.
   */
  const initializePointerListeners = () => {
    // General pointer events
    ['pointerdown', 'pointermove', 'pointerup', 'pointercancel'].forEach((eventType) => {
      useEventListener(svg, eventType, (event) => handlePointer(event, eventType.slice(7)), { passive: true });
    });

    // Resizing handles
    useEventListener(topHandle, 'pointerdown', () => { isResizing.value = 'up'; });
    useEventListener(rightHandle, 'pointerdown', () => { isResizing.value = 'right'; });
    useEventListener(bottomHandle, 'pointerdown', () => { isResizing.value = 'down'; });
    useEventListener(leftHandle, 'pointerdown', () => { isResizing.value = 'left'; });

    // Path nodes
    useEventListener(pathHandle1, 'pointerdown', () => { isPathNodeMoving.value = 'first'; });
    useEventListener(pathHandle2, 'pointerdown', () => { isPathNodeMoving.value = 'second'; });

    // Dragging selected location
    useEventListener(selectedLocationEl, 'pointerdown', () => {
      if (props.lockSelected) isDragging.value = true;
    });

    // Double-click or tap events for locations and paths
    ['dblclick', 'tap'].forEach((eventType) => {
      useEventListener(selectedLocationEl, eventType, () => {
        if (!props.lockSelected) emit('locationDblClicked', props.selectedLocation);
      });
      useEventListener(selectedPathEl, eventType, () => {
        if (!props.lockSelected) emit('pathDblClicked', props.selectedPath);
      });
    });

    // Wheel event listener for zooming
    useEventListener(svg, 'wheel', (event) => {
      if ((event.ctrlKey || event.metaKey) || !props.showZoomInstructions) {
        if (props.showZoomInstructions) zoomInstructions.value.style.display = 'none';
        zoomToPoint(1 + 0.01 * event.deltaY, event.clientX, event.clientY);
        event.preventDefault();
      } else if (props.showZoomInstructions) {
        if (zoomInstructions.value) {
          zoomInstructions.value.style.display = 'flex';
        }
        hideZoomInstructions();
      }
    });

    // Keydown event listener for hiding zoom instructions
    useEventListener(document, 'keydown', (event) => {
      if (event.ctrlKey || event.metaKey) {
        if (zoomInstructions.value) {
          zoomInstructions.value.style.display = 'none';
        }
      }
    });

    // Show coordinates on pointer move
    if (props.showCoords) {
      useEventListener(svg, 'pointermove', ({ clientX, clientY }) => {
        const { x, y } = screenToSvgCoordinates(clientX, clientY);
        svgX.value = x;
        svgY.value = y;
      });
    }
  };

  // Return exposed methods
  return { initializePointerListeners };
}
